import React, { useState, useEffect, useMemo } from 'react';
import { GoogleMap, LoadScript, Marker, Circle } from '@react-google-maps/api';
import Navbar from '../../components/Navbar';
import { adminAPI } from '../../services/api';

/**
 * Priority Zones — admin draws a geo-circle (center + radius) and orders the
 * restaurants to BOOST for users inside it, on the home browse + search. Zone
 * picks rank above the global home-priority; distance stays the tiebreaker.
 */
const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const AGRA = { lat: 27.1767, lng: 78.0081 };
const toRad = (d) => (d * Math.PI) / 180;
const haversineKm = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return Infinity;
  const R = 6371, dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};
const blank = () => ({ name: '', city: '', center: { ...AGRA }, radiusKm: 3, restaurants: [], active: true });

function PriorityZones() {
  const [zones, setZones] = useState([]);
  const [rests, setRests] = useState([]);
  const [draft, setDraft] = useState(null);          // zone being created/edited
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [z, r] = await Promise.all([adminAPI.listPriorityZones(), adminAPI.priorityZoneRestaurants()]);
      setZones(z.data?.data || []);
      setRests(r.data?.data || []);
    } catch (e) { setErr('Failed to load priority zones'); }
  };
  useEffect(() => { load(); }, []);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const restById = useMemo(() => new Map(rests.map((r) => [String(r._id), r])), [rests]);

  const startNew = () => { setDraft(blank()); setQ(''); setErr(''); };
  const edit = (z) => {
    setDraft({
      _id: z._id, name: z.name || '', city: z.city || '',
      center: { lat: z.center?.lat ?? AGRA.lat, lng: z.center?.lng ?? AGRA.lng },
      radiusKm: z.radiusKm || 3, restaurants: (z.restaurants || []).map(String), active: z.active !== false,
    });
    setQ(''); setErr('');
  };

  const save = async () => {
    if (!draft.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const body = { name: draft.name, city: draft.city, center: draft.center, radiusKm: Number(draft.radiusKm), restaurants: draft.restaurants, active: draft.active };
      if (draft._id) await adminAPI.updatePriorityZone(draft._id, body);
      else await adminAPI.createPriorityZone(body);
      setDraft(null); await load(); flash('Zone saved — live for that area.');
    } catch (e) { setErr(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const del = async (z) => {
    if (!window.confirm(`Delete zone "${z.name}"?`)) return;
    try { await adminAPI.deletePriorityZone(z._id); await load(); flash('Zone deleted.'); }
    catch (e) { setErr('Delete failed'); }
  };
  const toggleActive = async (z) => {
    try { await adminAPI.updatePriorityZone(z._id, { active: !(z.active !== false) }); await load(); }
    catch (e) { setErr('Update failed'); }
  };

  // ----- editor helpers -----
  const inRange = (r) => r.lat != null && haversineKm(draft.center, { lat: r.lat, lng: r.lng }) <= Number(draft.radiusKm);
  const addR = (id) => setDraft((d) => (d.restaurants.includes(id) ? d : { ...d, restaurants: [...d.restaurants, id] }));
  const removeR = (id) => setDraft((d) => ({ ...d, restaurants: d.restaurants.filter((x) => x !== id) }));
  const move = (i, dir) => setDraft((d) => {
    const a = [...d.restaurants]; const j = i + dir;
    if (j < 0 || j >= a.length) return d;
    [a[i], a[j]] = [a[j], a[i]]; return { ...d, restaurants: a };
  });

  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' };
  const input = { width: '100%', padding: '9px 11px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4 };
  const btn = { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
  const btnGhost = { ...btn, background: '#fff', color: '#2563EB', border: '1px solid #2563EB' };
  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#475569', borderBottom: '2px solid #E2E8F0' };
  const td = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid #EEF2F7' };

  const selectedRows = draft ? draft.restaurants.map((id) => restById.get(id)).filter(Boolean) : [];
  const candidates = useMemo(() => {
    if (!draft) return [];
    const sel = new Set(draft.restaurants);
    const term = q.trim().toLowerCase();
    return rests
      .filter((r) => !sel.has(String(r._id)))
      .filter((r) => !term || (r.name || '').toLowerCase().includes(term) || (r.city || '').toLowerCase().includes(term))
      .map((r) => ({ ...r, range: inRange(r), dist: haversineKm(draft.center, { lat: r.lat, lng: r.lng }) }))
      .sort((a, b) => (a.range === b.range ? a.dist - b.dist : (a.range ? -1 : 1)))
      .slice(0, 60);
    // eslint-disable-next-line
  }, [draft, rests, q]);

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8' }}>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: '0 0 4px' }}>Priority Zones</h1>
            <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>Draw an area (center + radius) and order the restaurants to boost for users inside it — on the home screen &amp; search. Zone picks rank above the global home-priority; nearest still breaks ties. Smallest zone wins on overlap.</p>
          </div>
          {!draft ? <button style={btn} onClick={startNew}>+ New zone</button> : null}
        </div>
        {msg ? <div style={{ ...card, background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534', padding: 10, marginTop: 12 }}>{msg}</div> : null}
        {err ? <div style={{ ...card, background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', padding: 10, marginTop: 12 }}>{err}</div> : null}

        {!draft ? (
          <div style={{ ...card, marginTop: 16, padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#F8FAFC' }}>
                <th style={th}>Zone</th><th style={th}>City</th><th style={th}>Center</th><th style={th}>Radius</th><th style={th}>Restaurants</th><th style={th}>Active</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {zones.map((z) => (
                  <tr key={z._id}>
                    <td style={{ ...td, fontWeight: 700 }}>{z.name}</td>
                    <td style={td}>{z.city || '—'}</td>
                    <td style={td}>{z.center?.lat?.toFixed(4)}, {z.center?.lng?.toFixed(4)}</td>
                    <td style={td}>{z.radiusKm} km</td>
                    <td style={td}>{(z.restaurants || []).length}</td>
                    <td style={td}><button onClick={() => toggleActive(z)} style={{ ...btnGhost, padding: '3px 10px', fontSize: 12, color: z.active !== false ? '#166534' : '#b91c1c', borderColor: z.active !== false ? '#166534' : '#b91c1c' }}>{z.active !== false ? 'Active' : 'Off'}</button></td>
                    <td style={td}>
                      <button onClick={() => edit(z)} style={{ ...btnGhost, padding: '3px 10px', fontSize: 12, marginRight: 6 }}>Edit</button>
                      <button onClick={() => del(z)} style={{ ...btnGhost, padding: '3px 10px', fontSize: 12, color: '#b91c1c', borderColor: '#fecaca' }}>Delete</button>
                    </td>
                  </tr>
                ))}
                {zones.length === 0 ? <tr><td style={td} colSpan={7}><span style={{ color: '#94a3b8' }}>No zones yet. Click “New zone”.</span></td></tr> : null}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px,1fr) minmax(320px,1fr)', gap: 16, marginTop: 16, alignItems: 'start' }}>
            {/* Left: details + map */}
            <div style={card}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                <div><label style={label}>Zone name</label><input style={input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Tajganj core" /></div>
                <div><label style={label}>City</label><input style={input} value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} placeholder="Agra" /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
                <div><label style={label}>Center lat</label><input style={input} type="number" step="0.0001" value={draft.center.lat} onChange={(e) => setDraft({ ...draft, center: { ...draft.center, lat: Number(e.target.value) } })} /></div>
                <div><label style={label}>Center lng</label><input style={input} type="number" step="0.0001" value={draft.center.lng} onChange={(e) => setDraft({ ...draft, center: { ...draft.center, lng: Number(e.target.value) } })} /></div>
                <div><label style={label}>Radius (km)</label><input style={input} type="number" step="0.5" min="0.5" value={draft.radiusKm} onChange={(e) => setDraft({ ...draft, radiusKm: e.target.value })} /></div>
              </div>
              <input type="range" min="0.5" max="25" step="0.5" value={draft.radiusKm} onChange={(e) => setDraft({ ...draft, radiusKm: e.target.value })} style={{ width: '100%', marginTop: 10 }} />
              <div style={{ marginTop: 10, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                {KEY ? (
                  <LoadScript googleMapsApiKey={KEY}>
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: 320 }}
                      center={draft.center} zoom={12}
                      onClick={(e) => setDraft({ ...draft, center: { lat: e.latLng.lat(), lng: e.latLng.lng() } })}
                    >
                      <Marker position={draft.center} draggable onDragEnd={(e) => setDraft({ ...draft, center: { lat: e.latLng.lat(), lng: e.latLng.lng() } })} />
                      <Circle center={draft.center} radius={Number(draft.radiusKm) * 1000} options={{ fillColor: '#2563EB', fillOpacity: 0.12, strokeColor: '#2563EB', strokeWeight: 1 }} />
                      {rests.filter((r) => r.lat != null && inRange(r)).slice(0, 80).map((r) => (
                        <Marker key={r._id} position={{ lat: r.lat, lng: r.lng }} title={r.name}
                          icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE, scale: 5, fillColor: draft.restaurants.includes(String(r._id)) ? '#16a34a' : '#f59e0b', fillOpacity: 1, strokeWeight: 0 }} />
                      ))}
                    </GoogleMap>
                  </LoadScript>
                ) : <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>Map key not configured — set center via lat/lng above. (Green/amber dots = in-range restaurants when the map loads.)</div>}
              </div>
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 8 }}>Click the map or drag the pin to set the center. Green dots = picked, amber = in-range not picked. {rests.filter((r) => r.lat != null && inRange(r)).length} restaurants in range.</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', marginTop: 10 }}>
                <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Active
              </label>
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button style={btn} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save zone'}</button>
                <button style={btnGhost} onClick={() => setDraft(null)}>Cancel</button>
              </div>
            </div>

            {/* Right: restaurant ordering + picker */}
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 8, color: '#0F172A' }}>Boosted restaurants ({selectedRows.length}) <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12 }}>— top = strongest</span></div>
              {selectedRows.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10 }}>None yet — add from the list below.</div> : (
                <div style={{ marginBottom: 12 }}>
                  {selectedRows.map((r, i) => (
                    <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderBottom: '1px solid #f2f2f2' }}>
                      <span style={{ width: 20, color: '#94a3b8', fontSize: 12 }}>{i + 1}.</span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{r.name} {inRange(r) ? null : <span title="outside the radius" style={{ color: '#b45309', fontSize: 11 }}>⚠ out of range</span>}</span>
                      <button onClick={() => move(i, -1)} style={{ ...btnGhost, padding: '2px 8px', fontSize: 12 }}>↑</button>
                      <button onClick={() => move(i, 1)} style={{ ...btnGhost, padding: '2px 8px', fontSize: 12 }}>↓</button>
                      <button onClick={() => removeR(String(r._id))} style={{ ...btnGhost, padding: '2px 8px', fontSize: 12, color: '#b91c1c', borderColor: '#fecaca' }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <label style={label}>Add restaurants <span style={{ fontWeight: 400, color: '#94a3b8' }}>(in-range first)</span></label>
              <input style={{ ...input, marginBottom: 8 }} placeholder="Search restaurant…" value={q} onChange={(e) => setQ(e.target.value)} />
              <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #EEF2F7', borderRadius: 8 }}>
                {candidates.map((r) => (
                  <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid #f6f6f6' }}>
                    <span style={{ flex: 1, fontSize: 13 }}>{r.name} <span style={{ color: '#94a3b8', fontSize: 11 }}>{r.city}{r.lat != null ? ` · ${r.dist.toFixed(1)}km` : ''}</span></span>
                    {r.range ? <span style={{ fontSize: 10, color: '#166534', background: '#dcfce7', padding: '1px 6px', borderRadius: 999 }}>in range</span> : null}
                    <button onClick={() => addR(String(r._id))} style={{ ...btnGhost, padding: '3px 10px', fontSize: 12 }}>Add</button>
                  </div>
                ))}
                {candidates.length === 0 ? <div style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>No matches.</div> : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PriorityZones;
